import { Link } from 'react-router-dom';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const stats = [
  { value: '12,000+', label: '收录影视作品' },
  { value: '860 万', label: '月活跃用户' },
  { value: '40+', label: '覆盖国家与地区' },
  { value: '24h', label: '内容更新频率' },
];

const values = [
  { icon: 'ri-compass-3-line', title: '内容为先', desc: '坚持以优质、真实的影视信息为核心，帮助用户快速找到真正值得看的内容。' },
  { icon: 'ri-user-heart-line', title: '用户体验', desc: '从搜索、筛选到浏览，每一处交互都为更顺畅的发现体验而设计。' },
  { icon: 'ri-shield-check-line', title: '规范透明', desc: '所有内容均来自公开资料整理，尊重版权，明确标注信息来源与免责说明。' },
  { icon: 'ri-lightbulb-flash-line', title: '持续进化', desc: '不断优化分类、推荐与资讯体系，让影视发现变得更有趣、更高效。' },
];

const milestones = [
  { year: '2021', title: '神马影院诞生', desc: '从一个简单的影视收藏清单开始，我们想给影迷一个更清爽的发现入口。' },
  { year: '2023', title: '内容体系升级', desc: '上线多维度分类筛选、评分与剧评体系，覆盖电影与电视剧两大频道。' },
  { year: '2025', title: '资讯与社区', desc: '引入行业资讯与用户剧评，让内容不止于信息，更有人与人的交流。' },
];

export default function AboutPage() {
  useDocumentMeta({
    title: '关于我们 - 神马影院',
    description: '了解神马影院：我们是一个面向电影与电视剧爱好者的综合影视资源资讯平台，致力于帮助用户发现、筛选并了解精彩影视内容。',
    keywords: '关于神马影院,神马影院介绍,影视资源平台,影视资讯网站',
    path: '/about',
  });

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
            <i className="ri-information-line"></i> 关于我们
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
            让每一次观影选择，都从一次轻松的发现开始
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            神马影院是一个综合性的影视资源资讯平台，面向广大电影与电视剧爱好者。我们不提供播放或下载服务，
            而是专注于把影视作品介绍得更清楚——从作品信息、剧情简介到观众剧评与相关推荐，
            帮助你在海量内容里快速找到真正想看的那一部。
          </p>

          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="rounded-xl border border-background-200 bg-background-50 p-5">
                <p className="font-heading text-2xl font-bold text-primary-600">{stat.value}</p>
                <p className="mt-1 text-sm text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <i className="ri-book-open-line text-accent-500"></i> 品牌故事
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-7 text-foreground-600">
              <p>
                神马影院最初只是一份影迷之间分享的“待看清单”。我们发现，真正让人头疼的从来不是没得看，
                而是不知道看什么——信息太碎、推荐太杂、筛选太麻烦。
              </p>
              <p>
                于是我们决定做一件事：把影视信息整理得清清楚楚。用类型、地区、年份这些最直觉的维度，
                加上评分、热度与真实剧评，让“找一部想看的电影”这件事重新变得简单。
              </p>
              <p>
                今天的神马影院，依然保持着当初的初心：内容为先、体验至上。我们希望它更像一位懂电影的朋友，
                而不是一个冷冰冰的列表。
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/category/movie"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-500"
              >
                <i className="ri-film-line"></i> 浏览电影
              </Link>
              <Link
                to="/contact"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-mail-send-line"></i> 联系我们
              </Link>
            </div>
          </div>

          <div>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <i className="ri-focus-3-line text-accent-500"></i> 我们坚持的理念
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {values.map((value) => (
                <div key={value.title} className="rounded-xl border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                    <i className={`${value.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{value.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <section className="mt-14">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
            <i className="ri-route-line text-accent-500"></i> 一路走来
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {milestones.map((item) => (
              <div key={item.year} className="relative rounded-xl border border-background-200 bg-background-50 p-5">
                <span className="inline-flex items-center rounded-full bg-secondary-500 px-3 py-1 text-xs font-bold text-foreground-950">
                  {item.year}
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-xl bg-primary-950 p-8 md:p-10">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl font-bold text-background-50">联系我们</h2>
              <p className="mt-2 text-sm leading-relaxed text-background-300">
                有任何建议、内容纠错或合作意向，欢迎随时通过以下方式与我们取得联系。
              </p>
              <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-background-200">
                <span className="flex items-center gap-2"><i className="ri-mail-line text-accent-400"></i> service@shenmatv.com</span>
                <span className="flex items-center gap-2"><i className="ri-phone-line text-accent-400"></i> 400-888-6666</span>
              </div>
            </div>
            <Link
              to="/contact"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 dark:text-foreground-950"
            >
              <i className="ri-chat-smile-2-line"></i> 前往反馈
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}